import { useId } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { RegraComissao } from '../../db/tipos'
import { pendenciasDeComissao } from '../../comissoes/completude'
import { produtosComPrecoNaRegra } from '../../comissoes/produtosVendaveis'
import { aoClicarComGuarda } from '../../componentes/guardaDeSaida'
import type { RegraRascunho } from '../../representadas/rascunhoDaPolitica'

const estiloCampo = 'border border-slate-300 rounded-lg px-2 py-1.5 text-sm w-full disabled:bg-slate-50'
const estiloRotulo = 'block text-[11px] text-slate-500 mb-0.5'

// Spec 27/09: so os CAMPOS da regra -- quem desenha o card (comissao padrao
// ou card da tabela) e TabelasEComissao. "Liberacao" so na regra PADRAO
// (smoke de 30/07: e da fornecedora, nao da tabela) e, desde 27/09, nem como
// texto nas tabelas. Muda so o rotulo: o valor gravado continua
// faturamento / pagamento_cliente.
export default function CardRegra({ representadaId, rascunho, regraSalva, erro, editavel, aoMudar }: {
  representadaId: string
  rascunho: RegraRascunho
  regraSalva: RegraComissao | undefined
  erro?: string
  editavel: boolean
  aoMudar: (p: Partial<RegraRascunho>) => void
}) {
  const idBase = useId()
  const ehPadrao = rascunho.tabelaPrecoId === null
  return (
    <div className="space-y-2">
      <div className={`grid gap-2 ${ehPadrao ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <div className="min-w-0">
          <label htmlFor={`${idBase}-tipo`} className={estiloRotulo}>Tipo de comissão</label>
          <select id={`${idBase}-tipo`} value={rascunho.tipo} disabled={!editavel}
            onChange={(e) => aoMudar({ tipo: e.target.value as RegraComissao['tipo'] })} className={estiloCampo}>
            <option value="pct_fixo">% fixo sobre o total</option>
            <option value="pct_por_item">% por produto/linha</option>
          </select>
        </div>
        <div className="min-w-0">
          <label htmlFor={`${idBase}-pct`} className={estiloRotulo}>% padrão</label>
          <input id={`${idBase}-pct`} value={rascunho.pctPadrao} disabled={!editavel}
            onChange={(e) => aoMudar({ pctPadrao: e.target.value })} inputMode="decimal" placeholder="ex.: 5" className={estiloCampo} />
        </div>
        {ehPadrao && (
          <div className="min-w-0">
            <label htmlFor={`${idBase}-gatilho`} className={estiloRotulo}>Liberação</label>
            <select id={`${idBase}-gatilho`} value={rascunho.gatilho} disabled={!editavel}
              onChange={(e) => aoMudar({ gatilho: e.target.value as RegraComissao['gatilho_liberacao'] })} className={estiloCampo}>
              <option value="faturamento">Faturamento</option>
              <option value="pagamento_cliente">Recebimento</option>
            </select>
          </div>
        )}
      </div>
      {erro && <p className="text-sm text-red-800 bg-red-100 rounded-lg px-3 py-2">{erro}</p>}
      {/* Percentuais em tela propria (pedido dela, 24/09). So para regra JA
          SALVA como % por produto/linha. */}
      {regraSalva && regraSalva.tipo === 'pct_por_item' && (
        <LinkPercentuais representadaId={representadaId} regra={regraSalva} />
      )}
    </div>
  )
}

// O aviso de produtos sem comissao vem junto no link -- e dinheiro, nao pode
// sumir de vista.
function LinkPercentuais({ representadaId, regra }: { representadaId: string; regra: RegraComissao }) {
  const navegar = useNavigate()
  const percentuais = useLiveQuery(
    () => banco.comissao_percentuais.where('regra_id').equals(regra.id).filter((p) => p.apagado_em === null).toArray(),
    [regra.id],
  )
  const vendaveis = useLiveQuery(
    () => produtosComPrecoNaRegra(representadaId, regra),
    [representadaId, regra.id, regra.tabela_preco_id, regra.tipo],
  )
  const pendentes = percentuais && vendaveis ? pendenciasDeComissao(regra, percentuais, vendaveis).length : 0
  // Guarda de saida e `?aba=comercial` antes de navegar: iguais aos de hoje
  // (fix round 1 do Task 13 e Task 16, escolha "2" dela).
  const destino = `/representadas/${representadaId}/comissao/${regra.id}/percentuais`
  const antesDeNavegar = () => navegar(`/representadas/${representadaId}?aba=comercial`, { replace: true })
  return (
    <Link to={destino} onClick={aoClicarComGuarda(navegar, destino, antesDeNavegar)}
      className="flex items-center gap-2 rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-900">
      <span className="min-w-0 flex-1">
        Percentuais por produto/linha: {percentuais?.length ?? '…'} cadastrado(s)
        {pendentes > 0 && <span className="text-amber-700"> · ⚠ {pendentes} sem comissão</span>}
      </span>
      <span className="shrink-0 rounded-lg border border-sky-700 px-2 py-1 font-medium">Ver detalhes ›</span>
    </Link>
  )
}
